import { useState } from "react"
import { CheckCheck, Clock, UserRoundPlus } from "lucide-react"
import { api } from "@/lib/api"
import { toast } from "@/components/portal/Toast"
import { UebergabeDialog } from "./UebergabeDialog"
import { anzeigeName, naechsterFreitag, tagInTagen, tagKurz, useUebergeben, useZusammenarbeitNeuLaden } from "./zusammenarbeit"

/**
 * Fußleiste der Mail im neuen Postfach: alles Seltene an genau einem Ort.
 *
 * Links „Erledigt“, „Später“ (Wiedervorlage) und „Zuweisen“ — dieselben
 * Wege wie die Leiste „Noch offen“ der bisherigen Ansicht. Rechts davon setzt
 * der Aufrufer die gewohnte Aktionsleiste (Antworten, Weiterleiten,
 * Verschieben, Wartet, Spam, ···), damit keine Funktion verloren geht.
 */
export function KarteFuss({ emailId, status, zustaendig, wiedervorlageAm, eingehend, onErledigt, children }: {
  emailId: number
  status?: string | null
  zustaendig?: string | null
  wiedervorlageAm?: string | null
  eingehend: boolean
  /** Nach „Erledigt“: das Postfach springt zur nächsten Mail. */
  onErledigt?: () => void
  children?: React.ReactNode
}) {
  const [wvOffen, setWvOffen] = useState(false)
  const [uebergabe, setUebergabe] = useState(false)
  const neu = useZusammenarbeitNeuLaden(emailId)
  const uebergeben = useUebergeben(emailId)

  const alsErledigt = async () => {
    const { error } = await api.PATCH("/api/v1/emails/{email_id}", {
      params: { path: { email_id: emailId } }, body: { status: "done" },
    })
    if (error) { toast("Konnte nicht als erledigt markieren", "error"); return }
    toast("Als erledigt abgelegt", "success")
    neu()
    onErledigt?.()
  }
  const wiedervorlage = (am: string | null) => {
    setWvOffen(false)
    uebergeben.mutate({ zustaendig: zustaendig ?? null, wiedervorlage_am: am, wiedervorlage_loeschen: am === null }, {
      onSuccess: () => toast(am ? `Wird am ${tagKurz(am)} wieder vorgelegt` : "Wiedervorlage entfernt", "success"),
      onError: () => toast("Wiedervorlage fehlgeschlagen", "error"),
    })
  }

  return (
    <div className="pk-fuss" data-testid="karte-fuss">
      {eingehend && status !== "done" && (
        <button type="button" className="pk-fuss-knopf" onClick={alsErledigt}
          title="Ohne weitere Schritte als erledigt ablegen">
          <CheckCheck className="w-4 h-4" aria-hidden /> Selbst erledigt
        </button>
      )}
      {eingehend && (
        <div style={{ position: "relative" }}>
          <button type="button" className="pk-fuss-knopf" onClick={() => setWvOffen(!wvOffen)} aria-expanded={wvOffen}
            title="Die Mail verschwindet bis zum gewählten Tag aus „Offen“">
            <Clock className="w-4 h-4" aria-hidden /> {wiedervorlageAm ? `Später: ${tagKurz(wiedervorlageAm)}` : "Später"}
          </button>
          {wvOffen && (
            <div className="ev2-popover pk-popover-oben" role="menu">
              <button type="button" role="menuitem" onClick={() => wiedervorlage(tagInTagen(1))}>Morgen</button>
              <button type="button" role="menuitem" onClick={() => wiedervorlage(naechsterFreitag())}>Freitag</button>
              <button type="button" role="menuitem" onClick={() => wiedervorlage(tagInTagen(7))}>In einer Woche</button>
              {wiedervorlageAm && <button type="button" role="menuitem" onClick={() => wiedervorlage(null)}>Entfernen</button>}
            </div>
          )}
        </div>
      )}
      {eingehend && (
        <button type="button" className="pk-fuss-knopf" onClick={() => setUebergabe(true)}
          title="Jemandem zuweisen, mit Notiz zum Stand">
          <UserRoundPlus className="w-4 h-4" aria-hidden /> {zustaendig ? anzeigeName(zustaendig) : "Zuweisen"}
        </button>
      )}
      <span style={{ flex: 1 }} />
      <div className="pk-fuss-aktionen">{children}</div>
      {uebergabe && (
        <UebergabeDialog emailId={emailId} offen onClose={() => setUebergabe(false)} aktuell={zustaendig} />
      )}
    </div>
  )
}
